<template>
  <div
    :style="{
      height: display.height - 47 + 'px',
      overflowY: 'auto',
    }"
  >
    <v-container class="page-wrapper text-left pa-3">
      <v-row class="h-100">
        <v-col cols="12" md="7" class="h-100">
          <div class="mb-5">
            <h1 class="text-h5 font-weight-bold mb-2">Complete order</h1>
            <p class="text-body-1 text-medium-emphasis mb-6">
              Double-check the details, then pay securely with Stripe. Your
              report will arrive instantly by email.
            </p>
          </div>
          <div
            class="text-caption font-weight-medium text-uppercase text-grey-darken-2"
          >
            REPORT TYPE
          </div>
          <v-card
            variant="outlined"
            class="pa-4 mb-4"
            style="border-color: #c7c5c5"
          >
            <v-card-actions class="pa-0">
              <div>
                <div class="text-subtitle-1 weight-600">
                  Instant IMD 2025 Report
                </div>
                <div class="text-caption">
                  Curated by our local area data experts
                </div>
              </div>
              <v-spacer />
              <div>
                <div class="font-weight-bold">£495.00</div>
                <div class="text-caption ml-1">excl. VAT</div>
              </div>
            </v-card-actions>
          </v-card>

          <div
            class="text-caption font-weight-medium text-uppercase text-grey-darken-2"
          >
            REPORT FOCUS AREA
          </div>
          <v-card variant="outlined" class="pa-0" style="border-color: #c7c5c5">
            <v-card-actions class="pa-4 border-b-sm">
              <v-icon size="24" icon="mdi-map-marker-outline" />
              <div v-if="!areaLoaded" class="ml-3 w-100">
                <v-skeleton-loader
                  height="50px"
                  type="list-item-two-line"
                ></v-skeleton-loader>
              </div>
              <div v-else class="ml-3">
                <div class="text-subtitle-1 weight-600">
                  {{ selectedArea.name }}
                </div>
                <div class="text-caption">
                  {{ selectedArea?.category_name ?? selectedArea.area_group }}
                </div>
              </div>
              <v-spacer />
              <v-btn
                @click="
                  $router.push({
                    name: 'ImdSelectArea',
                    query: { ...$route.query, session_id: undefined },
                  })
                "
                variant="text"
                rounded="0"
                class="weight-600"
                color="primary"
                >CHANGE</v-btn
              >
            </v-card-actions>
            <div :style="{ height: display.height - 422 + 'px !important' }">
              <v-card-text
                v-if="!areaLoaded"
                class="d-flex flex-column align-center justify-center h-100"
              >
                <v-icon size="50" color="grey-darken-1">mdi-map-marker</v-icon>
                <p class="text-grey-darken-1">
                  {{ invalidArea ? "Invalid Area" : "Loading Area..." }}
                </p>
              </v-card-text>
              <MapComponent
                v-show="areaLoaded"
                ref="myMap"
                :showMapControls="false"
                :roadMapBoundaryStyle="{
                  fillColor: '#273B77',
                  fillOpacity: 0.2,
                  strokeColor: '#273B77',
                  strokeWeight: 3,
                }"
                width="100%"
                height="100% !important"
                :disabledInteraction="true"
              />
            </div>
          </v-card>
        </v-col>

        <v-col cols="12" md="5" :class="{ 'mt-10': display.xs }">
          <v-form ref="form" validate-on="submit">
            <v-card
              class="pa-3"
              elevation="2"
              style="background-color: #f9f9f9"
            >
              <div class="font-weight-bold text-h6">Complete order</div>
              <p class="text-body-2 text-grey-darken-1 mb-4">
                Secure checkout powered by Stripe.
              </p>

              <div class="text-body-2 mb-2 font-weight-medium">
                Email address
              </div>
              <v-text-field
                v-model="email"
                variant="outlined"
                :rules="emailRules"
                placeholder="Email"
                density="compact"
                class="mb-2 imd-email-input"
                rounded="0"
              ></v-text-field>
              <p class="text-caption text-grey-darken-1 mb-4">
                We'll send the report to this address immediately after payment.
              </p>

              <v-checkbox
                v-model="termsAccepted"
                density="compact"
                hide-details
                :rules="[
                  (v) =>
                    v || 'You must agree to the End User Licence Agreement',
                ]"
              >
                <template v-slot:label>
                  <div class="text-body-2">
                    I agree to the
                    <a
                      href="https://ocsi.uk/products/on-demand/eula?utm_source=on-demand&utm_medium=buttont&utm_campaign=pre-sale"
                      target="_blank"
                      >End User Licence Agreement</a
                    >
                  </div>
                </template>
              </v-checkbox>
              <v-checkbox
                v-model="newsletterAccepted"
                density="compact"
                hide-details
              >
                <template v-slot:label>
                  <div class="text-body-2">
                    Keep me updated with the latest OCSI news, insights, and
                    offers
                  </div>
                </template>
              </v-checkbox>

              <v-divider class="my-4"></v-divider>

              <v-row dense>
                <v-col>Instant IMD 2025 Report</v-col>
                <v-col class="text-right font-weight-medium">£495.00</v-col>
              </v-row>
              <v-divider class="my-4"></v-divider>

              <v-row dense>
                <p class="text-body-2 text-medium-emphasis mb-2">
                  VAT will be calulated after you confirm your details in the
                  secure Stripe checkout
                </p>
              </v-row>
              <v-btn
                color="success"
                prepend-icon="mdi-lock"
                append-icon="mdi-arrow-right"
                block
                :disabled="!areaLoaded"
                rounded="0"
                size="large"
                class="mt-6"
                @click="startCheckout()"
              >
                Pay securely with stripe
              </v-btn>
              <p class="text-caption text-grey-darken-1 text-center mt-2">
                You'll only be charged once. Instant delivery after payment.
              </p>
            </v-card>
          </v-form>
        </v-col>
      </v-row>
    </v-container>

    <DynamicDialog
      v-model:show="stripeDialog"
      okBtnColor="success"
      persistent
      @dialogCancel="
        () => {
          checkoutInstance?.destroy();
          checkoutInstance = null;
        }
      "
      okBtnVariant="elevated"
      cancelBtnColor="none"
      cancelBtnVariant="text"
      max-width="412px"
      contentClass="pa-0"
      :hideFooter="true"
      :fullscreen="display.xs"
    >
      <template v-slot:title>Payment</template>
      <template v-slot:content>
        <div id="checkout">
          <!-- Checkout will insert the payment form here -->
        </div>
      </template>
    </DynamicDialog>
  </div>
</template>

<script>
import { useDisplay } from "vuetify";
import { loadStripe } from "@stripe/stripe-js";
import { systemMessages } from "@/mixins/SystemMessages";
import MapComponent from "../components/Map.vue";
import DynamicDialog from "../components/DynamicDialog.vue";

export default {
  name: "OnDemandCheckOut",
  mixins: [systemMessages],
  components: {
    MapComponent,
    DynamicDialog,
  },
  computed: {
    selectedArea: {
      get() {
        const area =
          this.$route.query?.custom_area ?? this.$route.query?.standard_area;

        return area ? JSON.parse(area) : null;
      },
      set(area) {
        if (area) {
          this.$router.push({
            query: {
              [area.category_name ? "custom_area" : "standard_area"]:
                JSON.stringify(area),
            },
          });
        }
      },
    },
  },
  data() {
    return {
      display: useDisplay(),
      areaLoaded: false,
      invalidArea: false,
      email: "",
      termsAccepted: false,
      newsletterAccepted: false,
      emailRules: [
        (v) => !!v || "Please enter your email address",
        (v) => /.+@.+\..+/.test(v) || "Please enter a valid email address",
      ],
      stripeDialog: false,
      checkoutInstance: null,
    };
  },
  mounted() {
    /*
      If there's a session_id in the URL, it means the user has been redirected back due to a failed/cancelled payment or 
       user reloaded the page during checkout which they aren't supposed to do, if so show a warning and validate the session.
    */
    if (this.$route.query?.session_id) {
      this.validateCheckoutSession();
    } else {
      // otherwise just validate the area that's in the URL
      this.validateArea();
    }

    // Send visit to analytics
    this.$axios.put("/send-analytics", {
      event: "on_demand_iod_checkout_visits",
    });
  },
  beforeUnmount() {
    // Clean up the Stripe checkout instance when the component is destroyed
    this.checkoutInstance?.destroy();
    this.checkoutInstance = null;
  },
  methods: {
    validateArea() {
      let area =
        this.$route.query?.custom_area ?? this.$route.query?.standard_area;

      if (area) {
        area = JSON.parse(area);

        this.$axios
          .post("/validate-selected-area", area)
          .then((response) => {
            this.selectedArea = response.data;
            this.plotSelectedAreaOnMap();
          })
          .catch((error) => {
            this.errorMsg({
              title: "Invalid Area",
              message: error.message,
            });
            this.invalidArea = true;
            this.$router.push({ name: "ImdSelectArea" });
          });
      }
    },
    async validateCheckoutSession() {
      const sessionID = this.$route.query?.session_id;

      // snackbar button
      const supportButton = {
        text: "contact support",
        callback: () => {
          window.location.href = `mailto:support@ocsi.co.uk?subject=Stripe session ID: ${sessionID}`;
        },
      };

      try {
        const response = await this.$axios.get(
          `check-purchase-status/${sessionID}`,
        );
        const { status } = response.data;

        const handlers = {
          complete: () => {
            this.successMsg({
              title: "Payment completed",
              message: "Your report is on its way! Redirecting you now.",
            });
            this.$router.push({
              name: "ThanksForPurchase",
              query: { session_id: sessionID },
            });
          },
          open: () => {
            this.warnMsg({
              title: "Could not complete checkout",
              message:
                "Please try again. If you've been charged but haven't received your report, contact support.",
              button: supportButton,
            });
            this.validateArea();
          },
          expired: () => {
            this.errorMsg({
              title: "Your checkout session has expired",
              message:
                "Please start over. If you've been charged but haven't received your report, contact support.",
              button: supportButton,
            });
            this.$router.push({ path: "/purchase/imd-report" });
          },
          default: () => {
            console.error(`Unknown session status: ${status}`);
            this.warnMsg({
              title: "Could not complete checkout",
              message:
                "Please try again. If you've been charged but haven't received your report, contact support.",
              button: supportButton,
            });
            this.$router.push({ path: "/purchase/imd-report" });
          },
        };

        (handlers[status] || handlers.default)();
      } catch (error) {
        this.errorMsg({
          title: "Couldn't validate checkout session",
          message: error.response?.data?.message || error.message,
        });
        this.$router.push({ path: "/purchase/imd-report" });
      }
    },
    async plotSelectedAreaOnMap() {
      let area =
        this.$route.query?.custom_area ?? this.$route.query?.standard_area;

      if (area) {
        area = JSON.parse(area);

        const displayAction = area.category_name // is custom area or standard area?
          ? "displayCustomAreas"
          : "displayStandardAreas";

        await this.$refs.myMap[displayAction](area.id);

        // zoom in
        const zoomAction = area.category_name
          ? "zoomInCustomAreas"
          : "zoomInStandardAreas";

        await this.$refs.myMap[zoomAction](area.id);

        this.areaLoaded = true;
      }
    },
    async startCheckout() {
      const { valid } = await this.$refs.form.validate();

      if (valid) {
        this.stripeDialog = true;

        const stripe = await loadStripe(
          process.env.VUE_APP_STRIPE_API_PUBLISHABLE_KEY,
        );

        // Start a new Stripe session by calling backend API
        const fetchClientSecret = async () => {
          try {
            var response = await this.$axios.post("/start-imd-checkout", {
              area: this.selectedArea,
              email: this.email,
              terms_of_service_accepted: this.termsAccepted,
              newsletter_opt_in: this.newsletterAccepted,
            });
          } catch (error) {
            this.errorMsg({
              title: "Couldn't start checkout session",
              message: error.response?.data?.message || error.message,
            });
            this.stripeDialog = false;
            throw error;
          }

          const { clientSecret } = response.data;

          return clientSecret;
        };

        // Initialize embedded checkout using the session ID
        this.checkoutInstance = await stripe.initEmbeddedCheckout({
          fetchClientSecret,
        });

        // load the iframe into the #checkout div
        this.checkoutInstance.mount("#checkout");
      }
    },
  },
};
</script>

<style scoped>
.page-wrapper {
  max-width: 1220px;
  margin: 0 auto;
  height: 97%;
}
</style>

<style>
.imd-email-input input {
  background-color: white !important;
}
</style>
